import { useState } from "react";
import { OrderLineTypes } from "@repo/database";
import { generateRandomString } from "@repo/utils";
import { addMilliseconds } from "date-fns";
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { CartItem } from "~/providers/cart-provider";
import { useCartManager } from "~/managers/use-cart-manager";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useForm,
} from "../ui/form";
import { ModalInput } from "../ui/modal-input";
import { RegisterButton } from "../ui/register-button";

export default function CartItemDiscountForm({
  item,
  onClose,
}: {
  item: CartItem;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");

  const form = useForm({
    schema: z.object({
      value: z.number(),
    }),
    defaultValues: {
      value: 0,
    },
  });
  const [percentageDiscount, setPercentageDiscount] = useState(0);

  const [newTotal, setNewTotal] = useState(0);

  const [total, setTotal] = useState(item.value);

  const { addToCart } = useCartManager();

  const handleSubmit = form.handleSubmit((data) => {
    if (total !== item.value) {
      void addToCart({
        ...item,
        value: total,
        printed: item.printed,
        ordered: false,
      });
    } else {
      if (data.value !== 0) {
        void addToCart({
          ordered: false,
          printed: false,
          type: OrderLineTypes.discount,
          createdAt: addMilliseconds(item.createdAt, 1),
          quantity: 1,
          name: "Discount",
          originalQuantity: 1,
          reference: generateRandomString(8),
          parentReference: item.reference,
          value: data.value,
          vatRate: 0,
        });
      }
    }
    onClose();
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <FormItem>
          <FormLabel>{t("Discount.total")}</FormLabel>
          <FormControl>
            <ModalInput
              type="decimal"
              value={total}
              onChange={(value) => {
                setTotal(value);
              }}
            />
          </FormControl>
        </FormItem>

        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Discount.fixed_discount")}</FormLabel>
              <FormControl>
                <ModalInput
                  type="decimal"
                  value={field.value}
                  onChange={(value) => {
                    const newTotal = item.value - value;
                    setPercentageDiscount(
                      Math.abs((newTotal - item.value) / item.value),
                    );
                    setNewTotal(newTotal);
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel>{t("Discount.percentage_discount")}</FormLabel>
          <ModalInput
            min={0}
            max={100}
            value={percentageDiscount}
            onChange={(value) => {
              setPercentageDiscount(value);

              // Calculate the new total as an integer
              const discountValue = new Decimal(item.value * (value / 100))
                .toDecimalPlaces(2)
                .toNumber();

              setNewTotal(discountValue);

              // Set the rounded discount value in the form
              form.setValue("value", discountValue);
            }}
            type="number"
          />
        </FormItem>
        <FormItem>
          <FormLabel>{t("Discount.new_total")}</FormLabel>
          <ModalInput
            type="decimal"
            value={newTotal}
            onChange={(value) => {
              setNewTotal(value);
              setPercentageDiscount(
                Math.abs(((value - item.value) / item.value) * 100),
              );
              form.setValue("value", item.value - value);
            }}
          />
        </FormItem>
        <RegisterButton type="submit" variant="success">
          {t("Discount.apply")}
        </RegisterButton>
      </form>
    </Form>
  );
}
